import {
  FILE_UPLOAD_LOCATION_QUALIFICATION_CODES,
  Qualification,
} from '@ps/survey-utils';
import { Option } from '@sapphire/result';

import {
  FeasibilityConditionNamesDto,
  FeasibilityCriteriaDto,
} from '../dtos/feasibility.dto';

/**
 * Feasibility addresses an uploaded location qualification (an FSA) by its db code and wants its
 * names as a flat list — unlike survey save, which keeps the qualification's own code. The payload
 * is built in qualification codes (flags and the quota overlay are keyed by them), so this is the
 * last step before the wire.
 *
 * A code the catalog cannot supply is left as it is: feasibility is advisory, so a missing entry
 * must not block the buyer.
 */
export class FeasibilityWireIdentity {
  private readonly _dbCodeByQualificationCode: ReadonlyMap<number, number>;

  constructor(catalogQualifications: readonly Qualification[]) {
    this._dbCodeByQualificationCode = new Map(
      catalogQualifications
        .filter(({ qualificationCode }) =>
          FILE_UPLOAD_LOCATION_QUALIFICATION_CODES.has(qualificationCode),
        )
        .map(({ qualificationCode, dbQualificationCode }) => [
          qualificationCode,
          dbQualificationCode,
        ]),
    );
  }

  public criteria(
    criteria: readonly FeasibilityCriteriaDto[],
  ): FeasibilityCriteriaDto[] {
    return criteria.map((criterion) => this._criterion(criterion));
  }

  private _criterion(
    criterion: FeasibilityCriteriaDto,
  ): FeasibilityCriteriaDto {
    return Option.from(
      this._dbCodeByQualificationCode.get(criterion.qualification_code),
    ).mapOr(criterion, (dbQualificationCode) => ({
      ...criterion,
      qualification_code: dbQualificationCode,
      ...Option.from(criterion.condition_names).mapOr({}, (names) => ({
        condition_names: _flatNames(names),
      })),
    }));
  }
}

function _flatNames(names: FeasibilityConditionNamesDto): string[] {
  return names.flatMap((name) =>
    typeof name === 'string' ? [name] : Object.values(name),
  );
}
